import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { NavLink, Link } from 'react-router-dom'
import { Dialog, Transition } from '@headlessui/react'
import {
  BookOpen,
  Calendar,
  DollarSign,
  Settings,
  Package,
  Boxes,
  FileText,
  FileBarChart,
  LogOut,
  Building2,
  Pencil,
  Ticket,
  Users,
  User,
  X,
  Search,
  MessageSquare,
  ClipboardList
} from 'lucide-react'

import useAuth from '../../../../utilities/hooks/useAuth'
import useUnreadMessages from '../../../../utilities/hooks/useUnreadMessages'
import { Badge } from '../../../ui/badge'

function ApplicationSidebar({ open = false, onClose = () => {} }) {
  const {
    isLoading: isAuthLoading,
    currentUser: { facilitiesUsers, students, id, superuser }
  } = useAuth()

  const { unreadCount } = useUnreadMessages()

  function navigationLink(item) {
    return (
      <NavLink
        key={item.name}
        to={item.destination}
        onClick={onClose}
        activeClassName="bg-purple-900 text-white opacity-95 shadow-inner"
        className="flex items-center px-3 py-2.5 text-sm font-medium text-purple-300 rounded-lg hover:text-purple-200 hover:bg-purple-800/50 transition-colors group relative"
      >
        <item.icon
          className="flex-shrink-0 w-5 h-5 text-purple-300 group-hover:text-purple-200 mr-3"
          aria-hidden="true"
        />
        <span className="flex-1">{item.name}</span>
        {item.badge && item.badge > 0 && (
          <Badge className="bg-purple-600 text-white hover:bg-purple-600">{item.badge}</Badge>
        )}
      </NavLink>
    )
  }

  function skeletonLink() {
    return (
      <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg">
        <div className="w-5 h-5 bg-purple-300/30 rounded animate-pulse" />
        <div className="h-4 bg-purple-300/30 rounded w-32 animate-pulse" />
      </div>
    )
  }

  function skeletonLinks() {
    return (
      <>
        {skeletonLink()}

        <div className="pt-4 mt-4 border-t border-purple-600">
          <div className="h-5 px-2 bg-purple-300/30 rounded w-32 mb-2 animate-pulse" />
        </div>
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={`skeleton-${index}`}>{skeletonLink()}</div>
        ))}
      </>
    )
  }

  function sidebarContent() {
    return isAuthLoading ? (
      skeletonLinks()
    ) : (
      <>
        {superuser && (
          <div className="pt-4 mt-4 space-y-1 border-t border-purple-600">
            <h3 className="px-3 mb-2 text-xs font-semibold tracking-wider text-purple-200 uppercase">
              ⚡️ God mode ⚡️
            </h3>
            {superUserLinks()}
          </div>
        )}
        {navigationLinks()}
        {facilitiesUsers.map((facilitiesUser) => (
          <div className="pt-4 mt-4 space-y-1 border-t border-purple-600" key={facilitiesUser.id}>
            <h3 className="px-3 mb-2 text-xs font-semibold tracking-wider text-purple-200 uppercase break-words">
              {facilitiesUser.facility.name}
            </h3>
            {facilityNavigationLinks(facilitiesUser)}
          </div>
        ))}
      </>
    )
  }

  function navigationLinks() {
    const navigation = []

    if (students && students.length > 0) {
      navigation.push(
        navigationLink({
          name: 'Student Dashboard',
          destination: `/guardians/${id}/students`,
          icon: Users
        })
      )
    }

    navigation.push(
      navigationLink({
        name: 'Messages',
        destination: '/messages',
        icon: MessageSquare,
        badge: unreadCount
      })
    )

    return navigation
  }

  function superUserLinks() {
    const navigation = [
      navigationLink({
        name: 'Guardian Lookup',
        destination: '/admin/guardians',
        icon: Search
      }),
      navigationLink({
        name: 'Facilities',
        destination: '/admin/facilities',
        icon: Building2
      })
    ]

    return navigation
  }

  function secondaryNavigationLinks() {
    const secondaryNavigation = [
      { name: 'Profile', destination: '/profile', icon: User },
      { name: 'Logout', destination: '/logout', icon: LogOut }
    ]

    return secondaryNavigation.map(navigationLink)
  }

  function facilityNavigationLinks({ coach, admin, facility: { id, providesFreeRentalSkates } }) {
    const facilitiesLinks = []
    const baseUrl = `/facilities/${id}`

    if (coach || admin) {
      facilitiesLinks.push({
        name: 'Calendar',
        destination: `${baseUrl}/calendar`,
        icon: Calendar
      })
      facilitiesLinks.push({
        name: 'Coaches Dashboard',
        destination: `${baseUrl}/coaches_dashboard`,
        icon: ClipboardList
      })
    }

    if (coach || admin) {
      facilitiesLinks.push({ name: 'Students', destination: `${baseUrl}/students`, icon: Users })
    }

    if (admin) {
      facilitiesLinks.push([
        { name: 'Staff', destination: `${baseUrl}/staff`, icon: Users },
        { name: 'Settings', destination: `${baseUrl}/edit`, icon: Settings },
        {
          name: 'Classes',
          destination: `${baseUrl}/classes`,
          icon: Boxes
        },
        {
          name: 'Class Packages',
          destination: `${baseUrl}/class_packages`,
          icon: Package
        },
        {
          name: 'Curriculums',
          destination: `${baseUrl}/curriculums`,
          icon: BookOpen
        },
        {
          name: 'Discounts',
          destination: `${baseUrl}/discounts`,
          icon: DollarSign
        },
        {
          name: 'Disclosures',
          destination: `${baseUrl}/disclosures`,
          icon: FileText
        },
        {
          name: 'Reports',
          destination: `${baseUrl}/reports`,
          icon: FileBarChart
        }
      ])

      if (providesFreeRentalSkates) {
        facilitiesLinks.push({
          name: 'Skate Rentals',
          destination: `${baseUrl}/skate_rentals`,
          icon: Ticket
        })
      }
    }

    facilitiesLinks.push({
      name: 'Register Students',
      destination: `${baseUrl}/register`,
      icon: Pencil
    })

    return facilitiesLinks.flat().map(navigationLink)
  }

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-40 flex lg:hidden print:hidden" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div
              className="relative flex flex-col flex-1 w-full max-w-xs pt-5 pb-4 bg-purple-700"
              data-cy="mobile-menu"
            >
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 p-1 -mr-14">
                  <button
                    type="button"
                    className="flex items-center justify-center w-12 h-12 rounded-full focus:outline-none focus:bg-gray-600 hover:bg-gray-700 transition-colors"
                    onClick={onClose}
                    data-cy="close-sidebar-button"
                  >
                    <X className="w-6 h-6 text-white" aria-hidden="true" />
                    <span className="sr-only">Close sidebar</span>
                  </button>
                </div>
              </Transition.Child>
              <div className="flex items-center flex-shrink-0 px-6 mb-6">
                <Link
                  to="/"
                  onClick={onClose}
                  className="text-3xl text-white font-wordmark font-bold hover:opacity-80 transition-opacity"
                >
                  Leevo
                </Link>
              </div>
              <div className="flex-1 h-0 overflow-y-auto scrollbar-hidden">
                <nav className="flex flex-col h-full px-3">
                  <div className="flex-1 space-y-1">{sidebarContent()}</div>
                  <div className="flex-shrink-0 block w-full pt-6 pb-20 space-y-1 border-t border-purple-600">
                    {secondaryNavigationLinks()}
                  </div>
                </nav>
              </div>
            </div>
          </Transition.Child>
          <div className="flex-shrink-0 w-14" aria-hidden="true" />
        </Dialog>
      </Transition.Root>

      <div className="hidden lg:flex lg:flex-shrink-0 print:hidden">
        <div className="flex flex-col w-64">
          <nav className="flex flex-col flex-grow pt-6 pb-4 h-auto overflow-y-auto scrollbar-hidden bg-purple-700 border-r border-purple-900">
            <div className="flex items-center flex-shrink-0 px-6 mb-6">
              <Link to="/" className="text-3xl text-white font-wordmark hover:opacity-80 transition-opacity">
                Leevo
              </Link>
            </div>
            <div className="flex flex-col flex-grow px-3">
              <div className="flex-1 space-y-1">{sidebarContent()}</div>
            </div>
            <div className="flex-shrink-0 block w-full px-3 pt-6 pb-20 space-y-1 border-t border-purple-600">
              {secondaryNavigationLinks()}
            </div>
          </nav>
        </div>
      </div>
    </>
  )
}

ApplicationSidebar.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func
}

export default ApplicationSidebar
